import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Navigate, useNavigate } from "react-router-dom";
import { localData } from "@/services/helper";
import MainLayout from "@/components/Layout";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "@/actions/authActions";
import NotAccessible from "@/components/NotAccessible";
import { usePermissions } from "@/hooks/usePermission";
import { role } from "../../utils/constants";
import Loader from "@/components/Loader";

interface RouteGuardProps {
    children: React.ReactNode;
    allowedRoles?: string[];
    noFooter?: boolean;
    permission?: "view" | "edit" | "add" | "delete";
}

interface PublicRouteProps {
    children: React.ReactNode;
    redirectTo?: string;
}

const isValidAuthData = (authData: unknown) => {
    if (!authData || typeof authData !== "object") {
        return false;
    }
    const data = authData as Record<string, unknown>;
    if (Object.keys(data).length === 0) {
        return false;
    }
    const hasId = typeof data.id === "string" && data.id.trim().length > 0;
    const hasEmail = typeof data.email === "string" && data.email.trim().length > 0;
    const hasPhone = typeof data.phoneNumber === "string" && data.phoneNumber.trim().length > 0;
    const hasToken = typeof data.token === "string" && data.token.trim().length > 0;
    return hasId || hasEmail || hasPhone || hasToken;
};

export const PublicRoute: React.FC<PublicRouteProps> = ({
    children,
    redirectTo = "/dashboard",
}) => {
    const hasAuthData = localData.get("AUTHDATA");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pathname = window.location.pathname;
    const url = window.location.href;
    const routes = useSelector((state: any) => state.getRoutes);
    const permissions = useSelector((state: any) => state.accessibleRoutes);
    const locationState = useLocation();
    const { getFirstAccessibleRoute } = usePermissions();
    const defaultRoute = useMemo(() => getFirstAccessibleRoute(), [getFirstAccessibleRoute]);

    useEffect(() => {
        const urlObj = new URL(url);
        const eventId = urlObj.searchParams.get("eventId");
        const action = urlObj.searchParams.get("action");
        const isDeepLink = urlObj.searchParams.get("dp") === "1";

        if (isDeepLink && eventId && action) {
            const deepLinkData = {
                eventId,
                action,
                timestamp: Date.now(),
            };
            localData.set("deepLinkNavigation", deepLinkData);
        }

        if (pathname === `/register/${role.STUDIO_MANAGER_ROLE}` || pathname === `/register/${role.STUDIO_PHOTOGRAPHER_ROLE}`) {
            const isResubmissionState = locationState.state?.isResubmission;

            if (isResubmissionState) {
                navigate("/login", { replace: true });
                return;
            }
        }
        if (pathname === "/register" || pathname === "/register/") {
            const urlObj = new URL(url);
            const invitationToken = urlObj.searchParams.get("invitationToken");
            if (invitationToken) {
                dispatch(authActions.logoutRequest());
                navigate("/register", {
                    state: {
                        invitationToken,
                    },
                });
            }
        }
    }, [dispatch, navigate, pathname, url]);

    if (isValidAuthData(hasAuthData)) {
        const deepLinkStr = localStorage.getItem("deepLinkNavigation");
        const urlObj = new URL(url);
        const eventIdParam = urlObj.searchParams.get("eventId");
        const assignmentIdParam = urlObj.searchParams.get("assignmentId");
        const actionParam = urlObj.searchParams.get("action");
        const isDeepLinkParam = urlObj.searchParams.get("dp") === "1";

        if (deepLinkStr || (isDeepLinkParam && eventIdParam && actionParam)) {
            try {
                let deepLink;

                if (deepLinkStr) {
                    deepLink = JSON.parse(deepLinkStr);
                } else {
                    deepLink = {
                        eventId: eventIdParam,
                        assignmentId: assignmentIdParam,
                        action: actionParam,
                        timestamp: Date.now(),
                    };
                }

                const MAX_CACHE_AGE = 24 * 60 * 60 * 1000;

                if (Date.now() - deepLink.timestamp < MAX_CACHE_AGE && deepLink.eventId) {
                    localData.remove("deepLinkNavigation");

                    setTimeout(() => {
                        navigate("/events/management", {
                            state: {
                                id: deepLink.eventId,
                                initialStep: 1,
                                assignmentId: deepLink.assignmentId,
                                action: deepLink.action,
                                fromDeepLink: true,
                            },
                            replace: true,
                        });
                    }, 0);

                    return null;
                } else {
                    localData.remove("deepLinkNavigation");
                }
            } catch (error) {
                console.error("Error processing deep link:", error);
                localData.remove("deepLinkNavigation");
            }
        }
        if (!routes?.list?.length && !permissions?.list?.length) {
            console.log("Loading routes or permissions11...");
            return <Loader />;
        }

        return <Navigate to={defaultRoute ?? redirectTo} replace />;
    }
    return <>{children}</>;
};

export const PrivateRoute: React.FC<RouteGuardProps> = ({ children, allowedRoles, noFooter }) => {
    const hasAuthData = localData.get("AUTHDATA");
    const location = useLocation();
    const { hasPermission } = usePermissions();
    const routes = useSelector((state: any) => state.getRoutes);
    const permissions = useSelector((state: any) => state.accessibleRoutes?.list?.routes);

    const skipPermissionCheck = (permissions?.length ?? 0) === 0 && permissions?.isFailure;

    if (!isValidAuthData(hasAuthData)) {
        const url = window.location.href;
        const urlObj = new URL(url);
        const eventId = urlObj.searchParams.get("eventId");
        const action = urlObj.searchParams.get("action");
        const isDeepLink = urlObj.searchParams.get("dp") === "1";

        if (isDeepLink && eventId && action) {
            const deepLinkData = {
                eventId,
                action,
                timestamp: Date.now(),
            };
            localData.set("deepLinkNavigation", JSON.stringify(deepLinkData));
        }   localData.remove

        return <Navigate to="/login" replace />;
    }

    const userRole = (hasAuthData as { role?: string }).role || "";

    const routesLoaded = Array.isArray(routes?.list);
    const permissionsLoaded = Array.isArray(permissions);

    if (
        userRole !== role.SUPER_ADMIN_ROLE &&
        (!routesLoaded || !permissionsLoaded) &&
        !skipPermissionCheck
    ) {
        console.log("Loading routes or permissions...");
        return <Loader />;
    }

    if (
        !skipPermissionCheck &&
        userRole !== role.SUPER_ADMIN_ROLE &&
        !hasPermission(location.pathname)
    ) {
        return <NotAccessible />;
    }

    return <MainLayout noFooter={noFooter}>{children}</MainLayout>;
};

export const ProtectedRoute = PrivateRoute;
